import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import toast from "react-hot-toast";
import QRCode from "qrcode";
import {
  FiCheck,
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiSliders,
} from "react-icons/fi";
import { BsQrCode } from "react-icons/bs";
import {
  downloadBlob,
  generateQrBlob,
} from "../../utils/qrImageHelper";

const FORMAT_OPTIONS = [
  { id: "png", label: "PNG", badge: "Lossless", desc: "Best for web, apps & transparent sharing" },
  { id: "svg", label: "SVG", badge: "Vector", desc: "Infinite scale, ideal for print & editing" },
  { id: "jpg", label: "JPG", badge: "Photo", desc: "Standard compact image with quality control" },
  { id: "webp", label: "WEBP", badge: "Modern", desc: "Smaller file size with high fidelity" },
  { id: "gif", label: "GIF", badge: "Graphic", desc: "Single-frame indexed color format" },
  { id: "bmp", label: "BMP", badge: "Bitmap", desc: "Uncompressed raw Windows bitmap" },
];

const RESOLUTION_OPTIONS = [
  { value: 256, label: "256 × 256", note: "Small (Web/Thumbnails)" },
  { value: 512, label: "512 × 512", note: "Medium (Standard / Social)" },
  { value: 1024, label: "1024 × 1024", note: "Large (High-Res / Display)" },
  { value: 2048, label: "2048 × 2048", note: "Ultra (Print / Posters)" },
];

const ERROR_LEVELS = [
  { value: "L", label: "L (7%)", desc: "Low recovery, smallest modules" },
  { value: "M", label: "M (15%)", desc: "Medium (recommended standard)" },
  { value: "Q", label: "Q (25%)", desc: "Quartile (good for rough surfaces)" },
  { value: "H", label: "H (30%)", desc: "High (best for print / outdoor)" },
];

const QrCodeModal = ({ show, onHide, shortUrl, title, shortCode }) => {
  const [format, setFormat] = useState("png");
  const [size, setSize] = useState(512);
  const [quality, setQuality] = useState(90); // 10 - 100 for JPG/WEBP
  const [errorCorrection, setErrorCorrection] = useState("M");
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [previewDataUrl, setPreviewDataUrl] = useState("");

  // Generate live preview data URL
  useEffect(() => {
    if (!show || !shortUrl) return;

    let isMounted = true;
    QRCode.toDataURL(shortUrl, {
      width: 480,
      margin: 2,
      errorCorrectionLevel: errorCorrection,
      color: {
        dark: fgColor,
        light: bgColor,
      },
    })
      .then((url) => {
        if (isMounted) setPreviewDataUrl(url);
      })
      .catch((err) => {
        if (isMounted) console.error("Error generating preview QR code:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [show, shortUrl, errorCorrection, fgColor, bgColor]);

  // Clean filename generator
  const getDownloadFilename = (ext) => {
    const rawName = (title || shortCode || "fxurl-qr")
      .toLowerCase()
      .replace(/[^a-z0-9_-]/gi, "-")
      .replace(/-+/g, "-")
      .slice(0, 40);
    return `${rawName}-qr.${ext}`;
  };

  const handleDownload = async () => {
    if (!shortUrl) return;
    setIsDownloading(true);
    try {
      const blob = await generateQrBlob({
        text: shortUrl,
        format,
        size,
        quality: quality / 100,
        errorCorrectionLevel: errorCorrection,
        fgColor,
        bgColor,
        margin: 2,
      });

      const filename = getDownloadFilename(format === "jpg" ? "jpg" : format);
      downloadBlob(blob, filename);
      toast.success(`Downloaded QR Code as ${format.toUpperCase()} (${size}×${size})`);
    } catch (err) {
      console.error("QR Code download error:", err);
      toast.error(`Download failed: ${err.message || "Unknown error"}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyImage = async () => {
    if (!shortUrl) return;
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        throw new Error("Clipboard image copy is not supported in this browser");
      }
      const blob = await generateQrBlob({
        text: shortUrl,
        format: "png",
        size: 512,
        errorCorrectionLevel: errorCorrection,
        fgColor,
        bgColor,
        margin: 2,
      });
      await navigator.clipboard.write([
        new window.ClipboardItem({ "image/png": blob }),
      ]);
      setIsCopied(true);
      toast.success("QR Code image copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2500);
    } catch (err) {
      console.error("Copy QR image failed:", err);
      toast.error(err.message || "Could not copy QR image");
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      aria-labelledby="qr-modal-title"
      className="qr-modal"
    >
      <Modal.Header closeButton className="qr-modal-header">
        <div className="d-flex align-items-center gap-2">
          <span className="qr-modal-icon-badge" aria-hidden="true">
            <BsQrCode />
          </span>
          <div>
            <Modal.Title id="qr-modal-title" className="h5 mb-0 fw-bold">
              QR Code &amp; Download
            </Modal.Title>
            <p className="qr-modal-subtitle text-muted mb-0 small">
              Customize resolution, image format &amp; quality options
            </p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="qr-modal-body p-4">
        <div className="row g-4">
          {/* Left Column: QR Code Preview */}
          <div className="col-12 col-md-5 d-flex flex-column align-items-center justify-content-center text-center">
            <div className="qr-preview-wrapper shadow-sm p-3 rounded-3 mb-3">
              {previewDataUrl ? (
                <img
                  src={previewDataUrl}
                  alt="QR Code preview"
                  className="qr-preview-image rounded-2"
                  style={{
                    width: "240px",
                    height: "240px",
                    display: "block",
                    background: bgColor,
                    imageRendering: "pixelated",
                  }}
                />
              ) : (
                <div
                  className="qr-preview-placeholder rounded-2"
                  style={{
                    width: "240px",
                    height: "240px",
                    background: "#f3f4f6",
                  }}
                />
              )}
            </div>

            <div className="qr-url-chip px-3 py-1 rounded-pill mb-3 small d-flex align-items-center gap-2">
              <span className="text-truncate" style={{ maxWidth: "200px" }}>
                {shortUrl}
              </span>
              <a
                href={shortUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open short link"
                className="text-secondary"
              >
                <FiExternalLink />
              </a>
            </div>

            <div className="d-flex gap-2 w-100 justify-content-center">
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={handleCopyImage}
              >
                {isCopied ? <FiCheck className="text-success" /> : <FiCopy />}
                <span>{isCopied ? "Copied Image" : "Copy Image"}</span>
              </Button>
            </div>
          </div>

          {/* Right Column: Format, Quality & Resolution Settings */}
          <div className="col-12 col-md-7">
            {/* Format Selection */}
            <div className="mb-3">
              <label className="form-label fw-semibold small mb-2 d-flex justify-content-between align-items-center">
                <span>Select Format</span>
                <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
                  SVG is scalable vector &middot; PNG is lossless
                </span>
              </label>
              <div className="qr-format-grid">
                {FORMAT_OPTIONS.map((opt) => {
                  const isSelected = format === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`qr-format-card ${isSelected ? "selected" : ""}`}
                      onClick={() => setFormat(opt.id)}
                    >
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <span className="qr-format-label">{opt.label}</span>
                        <span className="qr-format-badge">{opt.badge}</span>
                      </div>
                      <span className="qr-format-desc">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Resolution Selector */}
            <div className="mb-3">
              <label className="form-label fw-semibold small mb-1">
                Image Resolution / Size
              </label>
              <Form.Select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="form-select-sm"
              >
                {RESOLUTION_OPTIONS.map((res) => (
                  <option key={res.value} value={res.value}>
                    {res.label} — {res.note}
                  </option>
                ))}
              </Form.Select>
              {format === "svg" && (
                <span className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  SVG is vector-based; this determines its initial bounding dimensions.
                </span>
              )}
            </div>

            {/* Quality Slider (for JPG & WEBP) */}
            {(format === "jpg" || format === "webp") && (
              <div className="mb-3 qr-quality-box p-2 rounded-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label htmlFor="qr-quality-slider" className="form-label fw-semibold small mb-0">
                    Compression Quality ({format.toUpperCase()})
                  </label>
                  <span className="badge bg-primary">{quality}%</span>
                </div>
                <input
                  id="qr-quality-slider"
                  type="range"
                  className="form-range"
                  min="30"
                  max="100"
                  step="5"
                  value={quality}
                  onChange={(e) => setQuality(Number(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted" style={{ fontSize: "11px" }}>
                  <span>Smaller file (30%)</span>
                  <span>Balanced (80-90%)</span>
                  <span>Maximum (100%)</span>
                </div>
              </div>
            )}

            {/* Advanced Settings Toggle */}
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1 text-muted"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <FiSliders />
                <span>{showAdvanced ? "Hide Advanced Settings" : "More Settings (Colors & Error Correction)"}</span>
              </button>
            </div>

            {/* Advanced Settings Content */}
            {showAdvanced && (
              <div className="qr-advanced-panel p-3 rounded-2 mb-3 border">
                <div className="row g-2">
                  <div className="col-12 col-sm-6">
                    <label className="form-label small fw-semibold mb-1">
                      Error Correction
                    </label>
                    <Form.Select
                      size="sm"
                      value={errorCorrection}
                      onChange={(e) => setErrorCorrection(e.target.value)}
                    >
                      {ERROR_LEVELS.map((lvl) => (
                        <option key={lvl.value} value={lvl.value}>
                          {lvl.label} - {lvl.desc}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="col-6 col-sm-3">
                    <label className="form-label small fw-semibold mb-1">
                      QR Color
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color form-control-sm"
                        value={fgColor}
                        onChange={(e) => setFgColor(e.target.value)}
                        title="Choose QR Code Foreground Color"
                      />
                      <span className="small text-muted">{fgColor}</span>
                    </div>
                  </div>

                  <div className="col-6 col-sm-3">
                    <label className="form-label small fw-semibold mb-1">
                      Background
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color form-control-sm"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        title="Choose QR Code Background Color"
                      />
                      <span className="small text-muted">{bgColor}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="qr-modal-footer d-flex justify-content-between align-items-center">
        <span className="text-muted small">
          Output: <strong>{format.toUpperCase()}</strong> ({format === "svg" ? "Vector" : `${size}×${size}px`})
        </span>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={isDownloading}
            className="d-flex align-items-center gap-2 qr-download-btn"
          >
            <FiDownload />
            <span>
              {isDownloading
                ? "Generating..."
                : `Download ${format.toUpperCase()}`}
            </span>
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QrCodeModal;
